import React, { memo, useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { createUseStyles, Input, Select } from '@Sage/hoi-poi-ui';
import { CHART_TYPES } from '../../../../constants';
import { useWeb5Context } from 'lib/Web5Context';
import ChartSelector from './fields/ChartSelector';
import SelectWithEntity from './fields/SelectWithEntity';
import { getEntities } from 'lib/services/forceBI';

import styles from './styles';

const useStyles = createUseStyles(styles, 'Crud');

const defaultOptionsByField = { entity: [] };

const CrudFields = memo(({ data, onChange, schema, errors }) => {
    const classes = useStyles();
    const { t } = useWeb5Context();
    const isFirstRender = useRef(true);
    const [optionsByField, setOptionsByField] = useState(defaultOptionsByField);

    const handleOnChange = useCallback(
        ({ fieldId, field }) =>
            (value) => {
                onChange && onChange({ fieldId, value });
            },
        [onChange],
    );

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            if (!data?.['chartType']) {
                handleOnChange({ fieldId: 'chartType' })(CHART_TYPES.bar);
            }
            if (!optionsByField.entities?.length) {
                getEntities().then((entities) =>
                    setOptionsByField({ ...optionsByField, entity: entities }),
                );
            }
        }
    }, [handleOnChange, t, data, optionsByField]);

    const getField = useCallback(
        (field) => {
            const isReadOnly = field?.getIsReadOnly
                ? field.getIsReadOnly({ fieldId: field.id, data })
                : field?.isReadOnly || false;

            const isRequired = field?.getIsRequired
                ? field.getIsRequired({ fieldId: field.id, data })
                : field?.isRequired || false;

            const isHidden = field?.getIsHidden
                ? field.getIsHidden({ fieldId: field.id, data })
                : false;

            if (isHidden) return null;

            let fieldProps = {
                id: field.id,
                label: field?.label || '',
            };

            if (!field?.subFields?.length) {
                fieldProps = {
                    ...fieldProps,
                    isRequired: isRequired || false,
                    isReadOnly: isReadOnly,
                    placeholder: field?.placeholder,
                    onChange: handleOnChange({ fieldId: field.id, field }),
                    isFullWidth: true,
                    error: errors.includes(field.id) ? t('helptext_required_field') : '',
                    hint: field?.hint || '',
                };
            } else {
                fieldProps = {
                    ...fieldProps,
                    ...field,
                    onChange: handleOnChange,
                    error: errors.includes(field.id) ? t('helptext_required_field') : '',
                };
            }

            if (field.shouldRenderField && !field.shouldRenderField({ fieldId: field.id, data })) {
                return null;
            }

            switch (field?.type) {
                case 'chartSelector':
                    return (
                        <ChartSelector
                            key={field.id}
                            {...fieldProps}
                            value={data?.[field.id] || ''}
                            data={data}
                        />
                    );
                case 'text':
                    return (
                        <Input
                            key={field.id}
                            label={field.label}
                            {...fieldProps}
                            value={data?.[field?.id] || ''}
                        />
                    );
                case 'singleValueList':
                    if (field.options) fieldProps.options = field.options;
                    else if (optionsByField[field.id]) {
                        //to use preloaded options to reduce time of loading options
                        fieldProps.options = optionsByField[field.id];
                    }
                    if (field.loadCustomOptions) {
                        fieldProps.loadOptions = field.loadCustomOptions({ data, t });
                    } else if (
                        field.parentId &&
                        data?.[field.parentId]?.value &&
                        field.loadOptions
                    ) {
                        fieldProps.loadOptions = field.loadOptions(
                            data[field.parentId].value.toLowerCase(),
                        );
                    } else if (field.loadOptions) {
                        fieldProps.loadOptions = field.loadOptions;
                    }

                    if (typeof field.cacheOptions === 'boolean') {
                        fieldProps.cacheOptions = field.cacheOptions;
                    }

                    return (
                        <Select
                            key={field.id}
                            label={field.label}
                            placeholderReadOnly={field.placeholderReadOnly || ''}
                            {...fieldProps}
                            value={data?.[field.id]}
                        />
                    );
                case 'selectWithEntity':
                    if (field.parentId && data?.[field.parentId]?.value && field.loadOptions) {
                        fieldProps.loadOptions = field.loadOptions(
                            data[field.parentId].value.toLowerCase(),
                        );
                    }
                    return (
                        <SelectWithEntity
                            key={field.id}
                            {...fieldProps}
                            data={data}
                            subFields={field.subFields}
                        />
                    );
                default:
                    return null;
            }
        },
        [handleOnChange, data, errors, t, optionsByField],
    );

    const renderCrud = useMemo(() => {
        return schema.map((section, index) => {
            const fields = section.fields || [];
            return fields.map((field) => {
                return getField(field);
            });
        });
    }, [schema, getField]);

    return <div className={classes.root}>{renderCrud}</div>;
});

export default CrudFields;
